import styled from "@emotion/styled";
import { ExCheckUncontrolled, RULES_REQUIERD } from "@energyx-dev/energyx-ui";
import { AgreeCheckBoxSet } from "components/common/AgreeCheckBoxSet";
import { TERMS_TYPE } from "components/signup/helper";
import { hackleEventUtil } from "hackle/hackleEventUtil";
import useTranslation from "hooks/useTranslation";
import { ElementRef, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { THackleMemberTypeProps } from "types/hackle";
import { TServices, TTerms } from "types/terms/termsTypes";

type TProps = {
  className?: string;
  signupDivision?: "individual" | "corporation" | "";
};

export const SignUpTerms = ({ className = "", signupDivision = "" }: TProps) => {
  const {
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = useFormContext();
  const checkedAllRef = useRef<ElementRef<typeof ExCheckUncontrolled>>(null);
  const isEnglishPage = useTranslation();
  const urlParameter = window.document.referrer;
  const [fetchfrom, setFetchfrom] = useState<TServices>("ENERGYX");
  const [termType, settermType] = useState<TTerms>(TERMS_TYPE.JOIN);
  const handleOnChangeAll = (checked: boolean) => {
    if (checked) {
      hackleEventUtil<THackleMemberTypeProps>({
        key: "check_signUp_agree_all_terms",
        properties: { member_type: signupDivision === "individual" ? "일반" : "법인" },
      });
      setValue("signUpTerms", true);
      setValue("privacyPolicyTerms", true);
      setValue("marketing", true);
      clearErrors(["signUpTerms", "privacyPolicyTerms"]);
    } else {
      setValue("signUpTerms", false);
      setValue("privacyPolicyTerms", false);
      setValue("marketing", false);
    }
  };

  const handleOnChangeEachCheckbox = (name: "signUpTerms" | "privacyPolicyTerms" | "marketing") => {
    hackleEventUtil<THackleMemberTypeProps>({
      key: (() => {
        switch (name) {
          case "marketing":
            return "check_marketing_information_policy";
          case "privacyPolicyTerms":
            return "check_private_information_policy";
          default:
            return "check_signUp_policy";
        }
      })(),
      properties: { member_type: signupDivision === "individual" ? "일반" : "법인" },
    });
  };

  const watchSignUpTerms = watch("signUpTerms");
  const watchPrivacyPolicyTerms = watch("privacyPolicyTerms");
  const watchIsMarketing = watch("marketing");

  useEffect(() => {
    checkedAllRef.current &&
      checkedAllRef.current.setChecked(
        watchSignUpTerms || watchPrivacyPolicyTerms || watchIsMarketing,
        !(watchSignUpTerms && watchPrivacyPolicyTerms && watchIsMarketing)
      );
  }, [watchSignUpTerms, watchPrivacyPolicyTerms, watchIsMarketing]);

  useEffect(() => {
    if (urlParameter.includes("tomarch")) {
      setFetchfrom("TOMARCH");
      settermType(TERMS_TYPE.PRIVACY_USE_AGREEMENT);
    }

    if (urlParameter.includes("design")) {
      setFetchfrom("DESIGN_WHOS");
      settermType(TERMS_TYPE.PRIVACY_USE_AGREEMENT);
    }
  }, []);

  return (
    <Layout>
      <ExCheckUncontrolled
        label={isEnglishPage ? "I agree to all terms of this website." : "전체 약관 동의"}
        size="default"
        ref={checkedAllRef}
        onChange={(checked) => handleOnChangeAll(checked)}
        className={className}
      />
      <Hr />
      <ControlledCheckBoxesLayout>
        <AgreeCheckBoxSet
          name="signUpTerms"
          checkBoxLabel={
            isEnglishPage
              ? "I agree to the Terms of Use. (required)"
              : "서비스 이용약관 동의 (필수)"
          }
          size="small"
          fetchfrom={fetchfrom}
          termType={termType}
          signupCheckDivision={
            signupDivision === "individual" ? "indivisualSignUpTerms" : "corporationSignUpTerms"
          }
          callbackOnChange={() => handleOnChangeEachCheckbox("signUpTerms")}
          rules={RULES_REQUIERD}
          joinType="JOIN"
        />
        <AgreeCheckBoxSet
          name="privacyPolicyTerms"
          checkBoxLabel={
            isEnglishPage
              ? "I agree to the Privacy Policy. (required)"
              : "개인정보 수집 및 이용 동의 (필수)"
          }
          fetchfrom={fetchfrom}
          termType={TERMS_TYPE.PRIVACY_POLICY}
          size="small"
          callbackOnChange={() => handleOnChangeEachCheckbox("privacyPolicyTerms")}
          signupCheckDivision={
            signupDivision === "individual"
              ? "indivisualPrivacyPolicyTerms"
              : "corporationPrivacyPolicyTerms"
          }
          rules={RULES_REQUIERD}
        />
        <AgreeCheckBoxSet
          name="marketing"
          checkBoxLabel={
            isEnglishPage
              ? "I agree to receive promotional information. (optional)"
              : "광고성 정보 수신 및 마케팅 활용 동의 (선택)"
          }
          size="small"
          signupCheckDivision={
            signupDivision === "individual" ? "indivisualIsMarketing" : "corporationIsMarketing"
          }
          callbackOnChange={() => handleOnChangeEachCheckbox("marketing")}
          fetchfrom={fetchfrom}
          termType={TERMS_TYPE.NONE}
        />
      </ControlledCheckBoxesLayout>
      {(errors.signUpTerms || errors.privacyPolicyTerms) && (
        <ErrorCaption>
          <span className="xicon-">attention</span>
          <p>
            {isEnglishPage ? "Please accept our terms and conditions." : "약관에 동의해주세요."}
          </p>
        </ErrorCaption>
      )}
    </Layout>
  );
};

const Layout = styled.div`
  > label:first-of-type {
    width: 100%;
  }
`;

const ControlledCheckBoxesLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hr = styled.hr`
  height: 1px;
  border: none;
  background-color: ${({ theme }) => theme.color.mono[10]};
  margin: 16px 0;
`;

const ErrorCaption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  margin-top: 16px;
  background-color: ${({ theme }) => theme.color.red[10]};
  border-radius: 8px;

  .xicon- {
    font-size: 20px;
    color: ${({ theme }) => theme.color.red[60]};
  }
  p {
    ${({ theme }) => theme.font.Caption};
    color: ${({ theme }) => theme.color.red[60]};
  }
`;
